import cn from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import 'video.js/dist/video-js.css';

import type Player_ from 'video.js/dist/types/player';

export type Player = Player_;

export interface VideoPlayerProps {
  options: Partial<{
    autoplay: boolean | 'muted' | 'play' | 'any';
    controls: boolean;
    loop: boolean;
    muted: boolean;
    poster: string | undefined;
    preload: 'auto' | 'metadata' | 'none';
    aspectRatio: string;
    disablePictureInPicture: boolean;
    fluid: boolean;
    fullscreen: { options: { navigationUI: 'auto' | 'show' | 'hide' } };
    responsive: boolean;
    src: string;
    sources: { src: string; type: string }[];
    userActions: Partial<{
      click: boolean | ((e: Event) => void);
      doubleClick: boolean | ((e: Event) => void);
    }>;
    // Full list: https://videojs.com/guides/options/
    [key: string]: unknown;
  }>;
  onReady: (player: Player) => void;
  className?: string;
}

export const VideoJS = ({ options, onReady, className }: VideoPlayerProps) => {
  const [videoNode, setVideoNode] = useState<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);

  const setVideoRef = useCallback((newVideoNode: HTMLDivElement | null) => {
    return setVideoNode((oldVideoNode) => {
      // Dispose the Video.js player when the component unmounts or ref changes
      if (!newVideoNode || newVideoNode != oldVideoNode) {
        if (playerRef.current) {
          playerRef.current.dispose();
          playerRef.current = null;
        }
      }
      return newVideoNode;
    });
  }, []);

  useEffect(() => {
    if (!videoNode) {
      return;
    }

    let cancelLoad = false;
    async function loadPlayer() {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const { default: videojs } = await import('video.js');
      if (cancelLoad || !videoNode) {
        return;
      }
      const videoElement = document.createElement('video-js');
      videoNode.appendChild(videoElement);
      const player = videojs(videoElement, options, () => {
        onReady && onReady(player);
      });
      playerRef.current = player;
    }

    if (!playerRef.current) {
      loadPlayer();
      return () => {
        cancelLoad = true;
      };
    } else {
      playerRef.current.autoplay(options.autoplay);
      playerRef.current.poster(options.poster);
      playerRef.current.src(options.sources);
      return;
    }
  }, [videoNode, options, onReady]);

  return <div data-vjs-player ref={setVideoRef} className={cn(className)}></div>;
};
